import * as React from 'react';
import { Select } from 'antd';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@frontend/shadcn/components/ui/accordion';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@/shadcn/components/ui/sheet';
import { Button } from '@/shadcn/components/ui/button';
import { Checkbox } from '@/shadcn/components/ui/checkbox';
import { Input } from '@/shadcn/components/ui/input';
import { Label } from '@/shadcn/components/ui/label';
import {
  Select as SelectComponent,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/shadcn/components/ui/select';
import { OFFER_STATUS_TYPE, STATS_OFFER_TYPE } from '../../types/globalTypes';

interface OfferFiltersDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedFilters: OFFER_STATUS_TYPE[];
  setSelectedFilters: (filters: OFFER_STATUS_TYPE[]) => void;
  archiveOffer: boolean;
  selectedOfferType: STATS_OFFER_TYPE;
  setSelectedOfferType: (type: STATS_OFFER_TYPE) => void;
  programs: { id: number; title: string }[];
  selectedPrograms: number[];
  setSelectedPrograms: (ids: number[]) => void;
  numericFilters: Record<string, string[]>;
  setNumericFilters: (filters: Record<string, string[]>) => void;
  selectedStatusFilters: string[];
  setSelectedStatusFilters: (filters: string[]) => void;
}

interface NumericFilterSectionProps {
  title: string;
  columns: Array<{ id: string; label: string }>;
  localNumericFilters: Record<string, string[]>;
  handleNumericFilterChange: (columnId: string, operator: string, value: string) => void;
  handleNumericFilterChangeBetween: (columnId: string, operator: string, value1: string, value2: string) => void;
  handleClearNumericFilter: (columnId: string) => void;
  showCurrencyPrefix?: boolean;
}

const NumericFilterSection: React.FC<NumericFilterSectionProps> = ({
  title,
  columns,
  localNumericFilters,
  handleNumericFilterChange,
  handleNumericFilterChangeBetween,
  handleClearNumericFilter,
  showCurrencyPrefix = false,
}) => (
  <div>
    <Label className="text-sm font-medium mb-3 block">{title}</Label>
    <div className="flex flex-col gap-3">
      {columns.map((column) => {
        const currentFilter = localNumericFilters[column.id] || ['', ''];
        const [operator, value] = currentFilter;

        return (
          <div key={column.id} className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <Label>{column.label}</Label>
              {operator && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleClearNumericFilter(column.id)}
                  className="text-xs text-muted-foreground hover:text-destructive h-6 px-2"
                >
                  Clear
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <SelectComponent
                value={operator}
                onValueChange={(newOperator) => {
                  if (newOperator === 'between') {
                    handleNumericFilterChangeBetween(column.id, newOperator, '', '');
                  } else {
                    handleNumericFilterChange(column.id, newOperator, value);
                  }
                }}
              >
                <SelectTrigger className="w-32 h-8 text-sm">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gt">Greater than</SelectItem>
                  <SelectItem value="lt">Less than</SelectItem>
                  <SelectItem value="between">Between</SelectItem>
                  <SelectItem value="eq">Equal to</SelectItem>
                </SelectContent>
              </SelectComponent>
              {operator === 'between' ? (
                <div className="flex gap-2 flex-1">
                  <div className="flex-1 relative">
                    {showCurrencyPrefix && (
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">$</span>
                    )}
                    <Input
                      type="number"
                      value={value.split(',')[0] || ''}
                      onChange={(e) => {
                        const [, value2] = value.split(',');
                        const numValue = e.target.value;
                        if (numValue && isNaN(Number(numValue))) return;
                        handleNumericFilterChangeBetween(column.id, operator, e.target.value, value2 || '');
                      }}
                      className={`flex-1 h-8 text-xs ${showCurrencyPrefix ? 'pl-6' : ''}`}
                      placeholder="Min"
                    />
                  </div>
                  <div className="flex-1 relative">
                    {showCurrencyPrefix && (
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">$</span>
                    )}
                    <Input
                      type="number"
                      value={value.split(',')[1] || ''}
                      onChange={(e) => {
                        const [value1] = value.split(',');
                        handleNumericFilterChangeBetween(column.id, operator, value1 || '', e.target.value);
                      }}
                      className={`flex-1 h-8 text-xs ${showCurrencyPrefix ? 'pl-6' : ''}`}
                      placeholder="Max"
                    />
                  </div>
                </div>
              ) : (
                <div className="flex-1 relative">
                  {showCurrencyPrefix && (
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">$</span>
                  )}
                  <Input
                    type="number"
                    value={value}
                    onChange={(e) => handleNumericFilterChange(column.id, operator, e.target.value)}
                    className={`flex-1 h-8 text-xs ${showCurrencyPrefix ? 'pl-6' : ''}`}
                    placeholder="Value"
                  />
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

const numericColumns = [
  { id: 'clicks', label: 'Clicks' },
  { id: 'conversions', label: 'Conversions' },
  { id: 'salesAmount', label: 'Sales Amount' },
  { id: 'payoutEarned', label: 'Commissions Earned' },
  { id: 'payoutMade', label: 'Payouts' },
  { id: 'avgSale', label: 'Avg Sale' },
];

export function RefreshOfferFiltersDrawer({
  open,
  onOpenChange,
  selectedFilters,
  setSelectedFilters,
  archiveOffer,
  selectedOfferType,
  setSelectedOfferType,
  programs = [],
  selectedPrograms,
  setSelectedPrograms,
  numericFilters,
  setNumericFilters,
  selectedStatusFilters,
  setSelectedStatusFilters,
}: OfferFiltersDrawerProps) {
  const [tempSelectedFilters, setTempSelectedFilters] = React.useState<OFFER_STATUS_TYPE[]>(selectedFilters);
  const [tempOfferType, setTempOfferType] = React.useState<STATS_OFFER_TYPE>(selectedOfferType);
  const [tempSelectedPrograms, setTempSelectedPrograms] = React.useState<number[]>(selectedPrograms || []);
  const [localNumericFilters, setLocalNumericFilters] = React.useState<Record<string, string[]>>(numericFilters);
  const [localSelectedStatusFilters, setLocalSelectedStatusFilters] = React.useState<string[]>(selectedStatusFilters);

  // Update local state when props change
  React.useEffect(() => {
    setTempSelectedFilters(selectedFilters);
    setTempOfferType(selectedOfferType);
    setTempSelectedPrograms(selectedPrograms || []);
    setLocalNumericFilters(numericFilters);
    setLocalSelectedStatusFilters(selectedStatusFilters);
  }, [selectedFilters, selectedOfferType, selectedPrograms, numericFilters, selectedStatusFilters, open]);

  const filterOptions = [
    { label: 'Active', value: OFFER_STATUS_TYPE.ACTIVE },
    { label: 'Paused', value: OFFER_STATUS_TYPE.PAUSED },
    { label: 'Expired', value: OFFER_STATUS_TYPE.EXPIRED },
    ...(archiveOffer ? [{ label: 'Archived', value: OFFER_STATUS_TYPE.ARCHIVED }] : []),
  ];

  const handleNumericFilterChange = (columnId: string, operator: string, value: string) => {
    setLocalNumericFilters((prev) => ({
      ...prev,
      [columnId]: [operator, value]
    }));
  };

  const handleNumericFilterChangeBetween = (columnId: string, operator: string, value1: string, value2: string) => {
    setLocalNumericFilters((prev) => ({
      ...prev,
      [columnId]: [operator, `${value1},${value2}`]
    }));
  };

  const handleClearNumericFilter = (columnId: string) => {
    setLocalNumericFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[columnId];
      return newFilters;
    });
  };

  const hasInvalidNumericFilters = () => {
    return Object.entries(localNumericFilters).some(([_columnId, filter]) => {
      const [operator, value] = filter;

      // If there's no operator, the filter is incomplete and invalid
      if (!operator) return true;

      // If there's no value, the filter is incomplete and invalid
      if (!value) return true;

      if (operator === 'between') {
        const [value1, value2] = value.split(',');
        // Check if both values are provided and valid numbers
        return !value1 || !value2 || isNaN(Number(value1)) || isNaN(Number(value2));
      } else {
        // Check if value is a valid number
        return isNaN(Number(value));
      }
    });
  };

  function handleApply() {
    // If neither offer type is checked, set to ALL_OFFERS
    const isSalesChecked = tempOfferType === STATS_OFFER_TYPE.LINK_OFFER;
    const isPromoChecked = tempOfferType === STATS_OFFER_TYPE.PROMO_OFFER;
    let offerTypeToSet = tempOfferType;
    if (!isSalesChecked && !isPromoChecked) {
      offerTypeToSet = STATS_OFFER_TYPE.ALL_OFFERS;
    }
    setSelectedFilters(tempSelectedFilters);
    setSelectedOfferType(offerTypeToSet);
    setSelectedPrograms(tempSelectedPrograms);
    setNumericFilters(localNumericFilters);
    setSelectedStatusFilters(localSelectedStatusFilters);
    onOpenChange(false);
  }

  function handleCancel() {
    setTempSelectedFilters(selectedFilters);
    setTempOfferType(selectedOfferType);
    setTempSelectedPrograms(selectedPrograms || []);
    setLocalNumericFilters(numericFilters);
    setLocalSelectedStatusFilters(selectedStatusFilters);
    onOpenChange(false);
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex flex-col h-full p-0 gap-0">
        <SheetHeader className="p-4">
          <SheetTitle className="m-0">Filters</SheetTitle>
        </SheetHeader>
        <div className="border-b" />
        <div className="flex-1 px-4 flex flex-col gap-6 py-4 overflow-y-auto">
          <Accordion type="multiple" defaultValue={['offer-status', 'offer-type', 'connected-projects']} className="w-full">
            <AccordionItem value="offer-status" className="border-none">
              <AccordionTrigger className="py-2 hover:no-underline">
                <Label className="text-sm font-medium">Offer Status</Label>
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-2 pt-2">
                  {filterOptions.map((opt) => (
                    <label key={opt.value} className="flex items-center gap-2">
                      <Checkbox
                        checked={tempSelectedFilters.includes(opt.value)}
                        onCheckedChange={(checked) => {
                          setTempSelectedFilters(checked
                            ? [...tempSelectedFilters, opt.value]
                            : tempSelectedFilters.filter((v) => v !== opt.value)
                          );
                        }}
                      />
                      <span>{opt.label}</span>
                    </label>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>

            <div className="border-b" />

            <AccordionItem value="offer-type" className="border-none">
              <AccordionTrigger className="py-2 hover:no-underline">
                <Label className="text-sm font-medium">Offer Type</Label>
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-2 pt-2">
                  <label className="flex items-center gap-2">
                    <Checkbox
                      checked={tempOfferType === STATS_OFFER_TYPE.LINK_OFFER}
                      onCheckedChange={(checked) =>
                        setTempOfferType(checked ? STATS_OFFER_TYPE.LINK_OFFER : STATS_OFFER_TYPE.ALL_OFFERS)}
                    />
                    <span>Sales Link Offer</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <Checkbox
                      checked={tempOfferType === STATS_OFFER_TYPE.PROMO_OFFER}
                      onCheckedChange={(checked) =>
                        setTempOfferType(checked ? STATS_OFFER_TYPE.PROMO_OFFER : STATS_OFFER_TYPE.ALL_OFFERS)}
                    />
                    <span>Promo Code Offer</span>
                  </label>
                </div>
              </AccordionContent>
            </AccordionItem>

            <div className="border-b" />

            <div className="py-2">
              <Label className="text-sm font-medium">Connected Projects</Label>
              <div className="pt-2">
                <Select
                  mode="multiple"
                  showSearch
                  placeholder="Select Projects"
                  value={tempSelectedPrograms}
                  onChange={setTempSelectedPrograms}
                  className="w-full"
                  options={programs.map((p) => ({ value: p.id, label: p.title }))}
                  getPopupContainer={(triggerNode) => triggerNode.parentNode}
                  dropdownStyle={{ minHeight: 240, overflowY: 'auto' }}
                  filterOption={(input, option) =>
                    String(option?.label).toLowerCase().includes(input.toLowerCase())}
                />
              </div>
            </div>
            <div className="border-b" />
            <AccordionItem value="kpi-filters" className="border-none">
              <AccordionTrigger className="py-2 hover:no-underline">
                <Label className="text-sm font-medium">KPI Filters</Label>
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-4 pt-2">
                  <NumericFilterSection
                    title="Engagement"
                    columns={numericColumns.slice(0, 2)}
                    localNumericFilters={localNumericFilters}
                    handleNumericFilterChange={handleNumericFilterChange}
                    handleNumericFilterChangeBetween={handleNumericFilterChangeBetween}
                    handleClearNumericFilter={handleClearNumericFilter}
                  />
                  <div className="border-b" />
                  <NumericFilterSection
                    title="Financial"
                    columns={numericColumns.slice(2)}
                    localNumericFilters={localNumericFilters}
                    handleNumericFilterChange={handleNumericFilterChange}
                    handleNumericFilterChangeBetween={handleNumericFilterChangeBetween}
                    handleClearNumericFilter={handleClearNumericFilter}
                    showCurrencyPrefix
                  />
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
        <div className="border-t" />
        <SheetFooter className="justify-start sm:justify-start p-4">
          <Button onClick={handleApply} disabled={hasInvalidNumericFilters()}>Apply</Button>
          <Button variant="outline" onClick={handleCancel}>Cancel</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
